import { Clock, AlertCircle, MapPin, User, FileText } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarEvent } from '@/services/calendarService';
import { Link } from 'react-router-dom';

interface EventCardProps {
  event: CalendarEvent;
  onClick?: () => void;
}

export default function EventCard({ event, onClick }: EventCardProps) {
  const isAppointment = event.type === 'appointment';
  const isDeadline = event.type === 'practice_deadline';
  const isActivity = event.type === 'activity';

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${
        isAppointment ? 'bg-blue-50 border-blue-200' : 
        isActivity ? 'bg-purple-50 border-purple-200' :
        'bg-red-50 border-red-200'
      }`}
      onClick={onClick}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            {isAppointment ? (
              <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
            ) : isActivity ? (
              <AlertCircle className="h-5 w-5 text-purple-600 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            )}
            
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 mb-1">{event.title}</div>
              
              {isAppointment && !event.allDay && (
                <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                  <Clock className="h-3 w-3" />
                  <span>
                    {format(new Date(event.start), 'HH:mm', { locale: it })} - {format(new Date(event.end), 'HH:mm', { locale: it })}
                  </span>
                </div>
              )}
              
              {isDeadline && (
                <div className="text-sm text-gray-600 mb-1">
                  Scadenza: {format(new Date(event.start), 'dd MM yyyy', { locale: it })}
                </div>
              )}
              
              {(event.type === 'activity' || event.type === 'appointment') && event.start && (
                <div className="text-sm text-gray-600 mb-1">
                  {format(new Date(event.start), 'dd MM yyyy HH:mm', { locale: it })}
                </div>
              )}

              {event.description && (
                <div className="text-sm text-gray-500 mt-1 line-clamp-2">{event.description}</div>
              )}

              <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
                {event.client && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>{event.client}</span>
                  </div>
                )}
                {event.practice && (
                  <div className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    <span>{event.practice}</span>
                  </div>
                )}
                {event.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>

              {event.practiceType && (
                <div className="mt-2">
                  <Badge variant="outline" className="text-xs">
                    {event.practiceType}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {event.status && (
            <Badge
              variant={event.status === 'COMPLETED' ? 'default' : 'secondary'}
              className="shrink-0"
            >
              {event.status}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
}


